import React, { useState, useRef, useEffect } from 'react';
import { Phone, Loader2, MessageCircle, ArrowLeft, CheckCircle2 } from 'lucide-react';

interface PhoneVerificationSectionProps {
  onSendCode: (phoneNumber: string) => Promise<{ success: boolean; error?: string; bypassed?: boolean }>;
  onVerifyCode: (code: string) => Promise<{ success: boolean; error?: string; remainingAttempts?: number }>;
  onSuccess: () => void;
  onCancel: () => void;
  initialPhoneNumber?: string | null;
}

type Step = 'phone' | 'verify' | 'success';

export const PhoneVerificationSection: React.FC<PhoneVerificationSectionProps> = ({
  onSendCode,
  onVerifyCode,
  onSuccess,
  onCancel,
  initialPhoneNumber
}) => {
  const [step, setStep] = useState<Step>('phone');
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber || '+44 ');
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [resendCountdown, setResendCountdown] = useState(0);
  
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Countdown timer for resend
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  // Auto-close after success
  useEffect(() => {
    if (step === 'success') {
      const timer = setTimeout(() => {
        onSuccess();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [step, onSuccess]);

  const handleSendCode = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!phoneNumber.trim() || phoneNumber.trim() === '+44') {
      setError('Please enter a phone number');
      return;
    }

    setLoading(true);
    setError(null);
    

    try {
      const result = await onSendCode(phoneNumber.trim());
      
      if (result.success) {
        if (result.bypassed) {
          // Cost cap hit — phone number saved unverified, skip the code step.
          setStep('success');
          return;
        }
        setStep('verify');
        setResendCountdown(60);
        // Focus first code input
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
      } else {
        setError(result.error || 'Failed to send verification code');
      }
    } catch (err) {
      setError('Failed to send verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (codeToVerify?: string) => {
    const code = codeToVerify || verificationCode.join('');
    
    if (code.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await onVerifyCode(code);
      
      if (result.success) {
        setStep('success');
      } else {
        setError(result.error || 'Invalid verification code');
        // Clear the code inputs on error
        setVerificationCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      setError('Failed to verify code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeInput = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when complete - pass the code directly to avoid state timing issues
    if (value && index === 5 && newCode.every(d => d !== '')) {
      handleVerifyCode(newCode.join(''));
    }
  };

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setVerificationCode(pasted.split(''));
      inputRefs.current[5]?.focus();
      // Auto-submit - pass code directly
      setTimeout(() => handleVerifyCode(pasted), 100);
    }
  };

  const handleBack = () => {
    setStep('phone');
    setVerificationCode(['', '', '', '', '', '']);
    setError(null);
  };

  // Success State
  if (step === 'success') {
    return (
      <div className="text-center py-4">
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-3 rounded-xl w-14 h-14 mx-auto mb-3 shadow-lg flex items-center justify-center">
          <CheckCircle2 className="h-7 w-7 text-white" />
        </div>
        <p className="text-emerald-600 dark:text-emerald-400 font-medium">
          Phone Verified!
        </p>
      </div>
    );
  }

  // Verification Step
  if (step === 'verify') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <button
            onClick={handleBack}
            className="flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </button>
          <button
            onClick={onCancel}
            className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            Cancel
          </button>
        </div>

        <div className="text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Enter the 6-digit code we sent to
          </p>
          <p className="font-semibold text-gray-900 dark:text-white">
            {phoneNumber}
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-2.5">
            <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
          </div>
        )}

        {/* Code Input */}
        <div className="flex justify-center gap-2" onPaste={handleCodePaste}>
          {verificationCode.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleCodeInput(index, e.target.value)}
              onKeyDown={(e) => handleCodeKeyDown(index, e)}
              className="w-10 h-12 text-center text-xl font-bold border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 text-gray-900 dark:text-white"
            />
          ))}
        </div>

        {/* Verify Button */}
        <button
          onClick={() => handleVerifyCode()}
          disabled={loading || verificationCode.some(d => !d)}
          className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white py-2.5 px-4 rounded-xl font-semibold hover:from-emerald-600 hover:to-teal-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-md"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Verifying...</span>
            </>
          ) : (
            <span>Verify Code</span>
          )}
        </button>

        {/* Resend Code */}
        <div className="text-center">
          {resendCountdown > 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Resend code in {resendCountdown}s
            </p>
          ) : (
            <button
              onClick={() => handleSendCode()}
              disabled={loading}
              className="text-emerald-600 dark:text-emerald-400 text-sm font-medium hover:underline disabled:opacity-50"
            >
              Resend verification code
            </button>
          )}
        </div>
      </div>
    );
  }

  // Phone Entry Step
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <button
          onClick={onCancel}
          className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
        >
          Cancel
        </button>
      </div>


      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-2.5">
          <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
        </div>
      )}

      {/* Phone Input */}
      <form onSubmit={handleSendCode} className="space-y-3">
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
          <input
            type="tel"
            autoComplete="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-theme-primary focus:border-theme-primary transition-all duration-200 text-gray-900 dark:text-white"
            placeholder="+44 7700 900000"
          />
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Include your country code (e.g., +44 for UK)
        </p>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white py-2.5 px-4 rounded-xl font-semibold hover:from-emerald-600 hover:to-teal-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-md"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Sending...</span>
            </>
          ) : (
            <>
              <MessageCircle className="h-4 w-4" />
              <span>Send Verification Code</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
};
